import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  IndianRupee, RefreshCw, Search, User, Plus, Trash2,
  Truck, Building2, Landmark, ArrowRight, CheckCircle2,
  AlertCircle, Clock, Phone, Calendar, Hash, FileText, Loader2
} from "lucide-react";

const PharmeasyCODReconciliation = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState("trips");
  
  // Trips data
  const [trips, setTrips] = useState([]);
  const [tripsPage, setTripsPage] = useState(1);
  const [tripsTotalPages, setTripsTotalPages] = useState(1);
  const [tripsFilter, setTripsFilter] = useState("all");
  
  // Riders data
  const [riders, setRiders] = useState([]);
  
  // Handovers data
  const [handovers, setHandovers] = useState([]);
  const [handoversPage, setHandoversPage] = useState(1);
  
  // Bank deposits data
  const [deposits, setDeposits] = useState([]);
  const [depositsPage, setDepositsPage] = useState(1);
  
  // Client settlements data
  const [settlements, setSettlements] = useState([]);
  const [settlementsPage, setSettlementsPage] = useState(1);
  
  // Modals
  const [handoverModal, setHandoverModal] = useState(false);
  const [depositModal, setDepositModal] = useState(false);
  const [settlementModal, setSettlementModal] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  
  // Form data
  const [handoverForm, setHandoverForm] = useState({
    trip_id: "",
    amount: "",
    handed_over_by: "",
    received_by: "",
    notes: ""
  });
  
  const [depositForm, setDepositForm] = useState({
    amount: "",
    transaction_number: "",
    deposit_date: new Date().toISOString().split('T')[0],
    bank_name: "",
    notes: ""
  });
  
  const [settlementForm, setSettlementForm] = useState({
    amount: "",
    transaction_number: "",
    settlement_date: new Date().toISOString().split('T')[0],
    notes: ""
  });

  useEffect(() => {
    fetchSummary();
    fetchTrips();
    fetchRiders();
  }, []);

  useEffect(() => {
    fetchTrips();
  }, [tripsPage, tripsFilter]);

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-cod/summary`);
      setSummary(res.data);
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    }
  };

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: tripsPage.toString(),
        size: "20"
      });
      if (tripsFilter !== "all") {
        params.append("status", tripsFilter);
      }
      const res = await axios.get(`${API}/pharmeasy-cod/trips?${params}`);
      setTrips(res.data.data || []);
      setTripsTotalPages(res.data.total_pages || 1);
    } catch (error) {
      console.error("Failed to fetch trips:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRiders = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-cod/riders`);
      setRiders(res.data.data || []);
    } catch (error) {
      console.error("Failed to fetch riders:", error);
    }
  };

  const fetchHandovers = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-cod/hub-handovers?page=${handoversPage}&size=20`);
      setHandovers(res.data.data || []);
    } catch (error) {
      console.error("Failed to fetch handovers:", error);
    }
  };

  const fetchDeposits = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-cod/bank-deposits?page=${depositsPage}&size=20`);
      setDeposits(res.data.data || []);
    } catch (error) {
      console.error("Failed to fetch deposits:", error);
    }
  };

  const fetchSettlements = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-cod/client-settlements?page=${settlementsPage}&size=20`);
      setSettlements(res.data.data || []);
    } catch (error) {
      console.error("Failed to fetch settlements:", error);
    }
  };

  // Open handover modal for a trip
  const openHandoverModal = (trip) => {
    setSelectedTrip(trip);
    setHandoverForm({
      trip_id: trip.trip_id.toString(),
      amount: trip.cash_collected.toString(),
      handed_over_by: trip.rider_name || "",
      received_by: "",
      notes: ""
    });
    setHandoverModal(true);
  };

  // Submit handover
  const submitHandover = async () => {
    try {
      await axios.post(`${API}/pharmeasy-cod/hub-handover`, {
        trip_id: parseInt(handoverForm.trip_id),
        amount: parseFloat(handoverForm.amount),
        handed_over_by: handoverForm.handed_over_by,
        received_by: handoverForm.received_by,
        notes: handoverForm.notes || null
      });
      toast.success("Hub handover recorded successfully");
      setHandoverModal(false);
      fetchTrips();
      fetchSummary();
      fetchHandovers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record handover");
    }
  };

  // Submit bank deposit
  const submitDeposit = async () => {
    try {
      await axios.post(`${API}/pharmeasy-cod/bank-deposit`, {
        amount: parseFloat(depositForm.amount),
        transaction_number: depositForm.transaction_number,
        deposit_date: depositForm.deposit_date,
        bank_name: depositForm.bank_name || null,
        notes: depositForm.notes || null
      });
      toast.success("Bank deposit recorded successfully");
      setDepositModal(false);
      setDepositForm({
        amount: "",
        transaction_number: "",
        deposit_date: new Date().toISOString().split('T')[0],
        bank_name: "",
        notes: ""
      });
      fetchSummary();
      fetchDeposits();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record deposit");
    }
  };

  // Submit client settlement
  const submitSettlement = async () => {
    try {
      await axios.post(`${API}/pharmeasy-cod/client-settlement`, {
        amount: parseFloat(settlementForm.amount),
        transaction_number: settlementForm.transaction_number,
        settlement_date: settlementForm.settlement_date,
        notes: settlementForm.notes || null
      });
      toast.success("Client settlement recorded successfully");
      setSettlementModal(false);
      setSettlementForm({
        amount: "",
        transaction_number: "",
        settlement_date: new Date().toISOString().split('T')[0],
        notes: ""
      });
      fetchSummary();
      fetchSettlements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record settlement");
    }
  };

  // Delete handover
  const deleteHandover = async (id) => {
    if (!window.confirm("Are you sure you want to delete this handover record?")) return;
    try {
      await axios.delete(`${API}/pharmeasy-cod/hub-handover/${id}`);
      toast.success("Handover deleted");
      fetchTrips();
      fetchSummary();
      fetchHandovers();
    } catch (error) {
      toast.error("Failed to delete handover");
    }
  };

  // Delete deposit
  const deleteDeposit = async (id) => {
    if (!window.confirm("Are you sure you want to delete this deposit record?")) return;
    try {
      await axios.delete(`${API}/pharmeasy-cod/bank-deposit/${id}`);
      toast.success("Deposit deleted");
      fetchSummary();
      fetchDeposits();
    } catch (error) {
      toast.error("Failed to delete deposit");
    }
  };

  // Delete settlement
  const deleteSettlement = async (id) => {
    if (!window.confirm("Are you sure you want to delete this settlement record?")) return;
    try {
      await axios.delete(`${API}/pharmeasy-cod/client-settlement/${id}`);
      toast.success("Settlement deleted");
      fetchSummary();
      fetchSettlements();
    } catch (error) {
      toast.error("Failed to delete settlement");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Universal Loader
  if (loading) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout}>
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500" />
          <p className="text-slate-500 mt-4 text-lg">Loading COD Data...</p>
        </div>
      </PharmeasyLayout>
    );
  }

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="pharmeasy-cod-reconciliation">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">COD Reconciliation</h1>
            <p className="text-slate-500">Track cash collection, handover, and settlements</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => { fetchSummary(); fetchTrips(); fetchRiders(); }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards - COD Flow Pipeline */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Stage 1: Rider Collection */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-blue-600 font-medium">STAGE 1</p>
                  <p className="text-sm font-semibold text-blue-800">Rider Collection</p>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-xs text-blue-600">Cash Collected</span>
                  <span className="text-sm font-bold text-blue-800">
                    {formatCurrency(summary?.collection?.cash_collected)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-blue-600">QR Collected</span>
                  <span className="text-sm font-medium text-blue-700">
                    {formatCurrency(summary?.collection?.qr_collected)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stage 2: Hub Handover */}
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-amber-600 font-medium">STAGE 2</p>
                  <p className="text-sm font-semibold text-amber-800">Hub Handover</p>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-xs text-amber-600">Handed Over</span>
                  <span className="text-sm font-bold text-amber-800">
                    {formatCurrency(summary?.hub_handover?.total_handed_over)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-amber-600">Pending</span>
                  <span className={`text-sm font-medium ${(summary?.hub_handover?.pending_handover || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(summary?.hub_handover?.pending_handover)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stage 3: Bank Deposit */}
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center">
                  <Landmark className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-purple-600 font-medium">STAGE 3</p>
                  <p className="text-sm font-semibold text-purple-800">Bank Deposit</p>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-xs text-purple-600">Deposited</span>
                  <span className="text-sm font-bold text-purple-800">
                    {formatCurrency(summary?.bank_deposit?.total_deposited)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-purple-600">Pending</span>
                  <span className={`text-sm font-medium ${(summary?.bank_deposit?.pending_deposit || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(summary?.bank_deposit?.pending_deposit)}
                  </span>
                </div>
              </div>
              <Button 
                size="sm" 
                className="w-full mt-3 bg-purple-600 hover:bg-purple-700"
                onClick={() => setDepositModal(true)}
              >
                <Plus className="w-3 h-3 mr-1" /> Add Deposit
              </Button>
            </CardContent>
          </Card>

          {/* Stage 4: Client Settlement */}
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-green-600 font-medium">STAGE 4</p>
                  <p className="text-sm font-semibold text-green-800">Client Settlement</p>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-xs text-green-600">Settled</span>
                  <span className="text-sm font-bold text-green-800">
                    {formatCurrency(summary?.client_settlement?.total_settled)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-green-600">Pending</span>
                  <span className={`text-sm font-medium ${(summary?.client_settlement?.pending_settlement || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(summary?.client_settlement?.pending_settlement)}
                  </span>
                </div>
              </div>
              <Button 
                size="sm" 
                className="w-full mt-3 bg-green-600 hover:bg-green-700"
                onClick={() => setSettlementModal(true)}
              >
                <Plus className="w-3 h-3 mr-1" /> Add Settlement
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => {
          setActiveTab(v);
          if (v === "handovers") fetchHandovers();
          if (v === "deposits") fetchDeposits();
          if (v === "settlements") fetchSettlements();
        }}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="trips">Trip-wise COD</TabsTrigger>
            <TabsTrigger value="riders">Rider Summary</TabsTrigger>
            <TabsTrigger value="handovers">Hub Handovers</TabsTrigger>
            <TabsTrigger value="deposits">Bank Deposits</TabsTrigger>
            <TabsTrigger value="settlements">Client Settlements</TabsTrigger>
          </TabsList>

          {/* Trip-wise COD Tab */}
          <TabsContent value="trips">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Trip-wise Cash Collection</CardTitle>
                  <Select value={tripsFilter} onValueChange={setTripsFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Trips</SelectItem>
                      <SelectItem value="pending_handover">Pending Handover</SelectItem>
                      <SelectItem value="handed_over">Handed Over</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : trips.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    No trips with cash collection found
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Trip ID</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Rider</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Date</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Cash (₹)</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">QR (₹)</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Total</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Handover Status</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trips.map((trip) => (
                          <tr key={trip.trip_id} className="border-b hover:bg-slate-50">
                            <td className="px-4 py-3 align-middle">
                              <span className="font-mono font-medium">{trip.trip_id}</span>
                            </td>
                            <td className="px-4 py-3 align-middle">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                <div>
                                  <p className="font-medium">{trip.rider_name || "Unknown"}</p>
                                  {trip.rider_phone && (
                                    <p className="text-xs text-slate-500">{trip.rider_phone}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 align-middle text-slate-600 whitespace-nowrap">
                              {formatDate(trip.trip_date)}
                            </td>
                            <td className="px-4 py-3 align-middle text-right font-medium text-blue-600">
                              {formatCurrency(trip.cash_collected)}
                            </td>
                            <td className="px-4 py-3 align-middle text-right font-medium text-purple-600">
                              {formatCurrency(trip.qr_collected)}
                            </td>
                            <td className="px-4 py-3 align-middle text-right font-medium">
                              {formatCurrency(trip.total_collected)}
                            </td>
                            <td className="px-4 py-3 align-middle text-center">
                              {trip.handover_status === "handed_over" ? (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Handed Over
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-700">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 align-middle text-center">
                              {trip.handover_status === "pending" && trip.cash_collected > 0 ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                                  onClick={() => openHandoverModal(trip)}
                                >
                                  <Building2 className="w-3 h-3 mr-1" />
                                  Record Handover
                                </Button>
                              ) : trip.handover ? (
                                <div className="text-sm text-slate-500">
                                  {formatCurrency(trip.handover.amount)} to {trip.handover.received_by}
                                </div>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {tripsTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-slate-500">Page {tripsPage} of {tripsTotalPages}</p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={tripsPage <= 1}
                        onClick={() => setTripsPage(p => p - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={tripsPage >= tripsTotalPages}
                        onClick={() => setTripsPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rider Summary Tab */}
          <TabsContent value="riders">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Rider-wise Summary</CardTitle>
              </CardHeader>
              <CardContent>
                {riders.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    No rider data available
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Rider</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Trips</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Cash Collected</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">QR Collected</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Handed Over</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Pending</th>
                        </tr>
                      </thead>
                      <tbody>
                        {riders.map((rider, idx) => (
                          <tr key={idx} className="border-b hover:bg-slate-50">
                            <td className="px-4 py-3 align-middle">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                                  <User className="w-4 h-4 text-slate-600" />
                                </div>
                                <div>
                                  <p className="font-medium">{rider.rider_name || "Unknown"}</p>
                                  {rider.rider_phone && (
                                    <p className="text-xs text-slate-500">{rider.rider_phone}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 align-middle text-center">
                              <Badge variant="outline">{rider.trip_count}</Badge>
                            </td>
                            <td className="px-4 py-3 align-middle text-right font-medium text-blue-600">
                              {formatCurrency(rider.total_cash_collected)}
                            </td>
                            <td className="px-4 py-3 align-middle text-right font-medium text-purple-600">
                              {formatCurrency(rider.total_qr_collected)}
                            </td>
                            <td className="px-4 py-3 align-middle text-right font-medium text-green-600">
                              {formatCurrency(rider.total_handed_over)}
                            </td>
                            <td className="px-4 py-3 align-middle text-right">
                              <span className={`font-medium ${rider.pending_handover > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {formatCurrency(rider.pending_handover)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hub Handovers Tab */}
          <TabsContent value="handovers">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Hub Handover Records</CardTitle>
              </CardHeader>
              <CardContent>
                {handovers.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    No handover records yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Trip ID</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Handed Over By</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Received By</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Amount</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Date</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Notes</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {handovers.map((h) => (
                          <tr key={h._id} className="border-b hover:bg-slate-50">
                            <td className="px-4 py-3 align-middle font-mono">{h.trip_id}</td>
                            <td className="px-4 py-3 align-middle">{h.handed_over_by}</td>
                            <td className="px-4 py-3 align-middle">{h.received_by}</td>
                            <td className="px-4 py-3 align-middle text-right font-medium">{formatCurrency(h.amount)}</td>
                            <td className="px-4 py-3 align-middle text-slate-600 whitespace-nowrap">{formatDate(h.created_at)}</td>
                            <td className="px-4 py-3 align-middle text-slate-500 text-sm max-w-[200px] truncate">{h.notes || "—"}</td>
                            <td className="px-4 py-3 align-middle text-center">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => deleteHandover(h._id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bank Deposits Tab */}
          <TabsContent value="deposits">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Bank Deposit Records</CardTitle>
                <Button onClick={() => setDepositModal(true)}>
                  <Plus className="w-4 h-4 mr-2" /> Add Deposit
                </Button>
              </CardHeader>
              <CardContent>
                {deposits.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    No deposit records yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Transaction #</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Amount</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Deposit Date</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Bank</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Notes</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Created By</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deposits.map((d) => (
                          <tr key={d._id} className="border-b hover:bg-slate-50">
                            <td className="px-4 py-3 align-middle font-mono font-medium">{d.transaction_number}</td>
                            <td className="px-4 py-3 align-middle text-right font-medium text-purple-600">{formatCurrency(d.amount)}</td>
                            <td className="px-4 py-3 align-middle whitespace-nowrap">{formatDate(d.deposit_date)}</td>
                            <td className="px-4 py-3 align-middle">{d.bank_name || "—"}</td>
                            <td className="px-4 py-3 align-middle text-slate-500 text-sm max-w-[200px] truncate">{d.notes || "—"}</td>
                            <td className="px-4 py-3 align-middle text-slate-600 text-sm">{d.created_by}</td>
                            <td className="px-4 py-3 align-middle text-center">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => deleteDeposit(d._id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Client Settlements Tab */}
          <TabsContent value="settlements">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Client Settlement Records</CardTitle>
                <Button onClick={() => setSettlementModal(true)}>
                  <Plus className="w-4 h-4 mr-2" /> Add Settlement
                </Button>
              </CardHeader>
              <CardContent>
                {settlements.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    No settlement records yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Transaction #</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Amount</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Settlement Date</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Notes</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Created By</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {settlements.map((s) => (
                          <tr key={s._id} className="border-b hover:bg-slate-50">
                            <td className="px-4 py-3 align-middle font-mono font-medium">{s.transaction_number}</td>
                            <td className="px-4 py-3 align-middle text-right font-medium text-green-600">{formatCurrency(s.amount)}</td>
                            <td className="px-4 py-3 align-middle whitespace-nowrap">{formatDate(s.settlement_date)}</td>
                            <td className="px-4 py-3 align-middle text-slate-500 text-sm max-w-[200px] truncate">{s.notes || "—"}</td>
                            <td className="px-4 py-3 align-middle text-slate-600 text-sm">{s.created_by}</td>
                            <td className="px-4 py-3 align-middle text-center">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => deleteSettlement(s._id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Hub Handover Modal */}
      <Dialog open={handoverModal} onOpenChange={setHandoverModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Hub Handover</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Trip ID</Label>
                <Input value={handoverForm.trip_id} disabled className="bg-slate-50" />
              </div>
              <div>
                <Label>Amount (₹)</Label>
                <Input 
                  type="number"
                  value={handoverForm.amount}
                  onChange={(e) => setHandoverForm({...handoverForm, amount: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Handed Over By (Rider)</Label>
                <Input 
                  value={handoverForm.handed_over_by}
                  onChange={(e) => setHandoverForm({...handoverForm, handed_over_by: e.target.value})}
                  placeholder="Rider name"
                />
              </div>
              <div>
                <Label>Received By (Hub Manager)</Label>
                <Input 
                  value={handoverForm.received_by}
                  onChange={(e) => setHandoverForm({...handoverForm, received_by: e.target.value})}
                  placeholder="Hub manager name"
                />
              </div>
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea 
                value={handoverForm.notes}
                onChange={(e) => setHandoverForm({...handoverForm, notes: e.target.value})}
                placeholder="Any additional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHandoverModal(false)}>Cancel</Button>
            <Button onClick={submitHandover} disabled={!handoverForm.amount || !handoverForm.received_by}>
              Record Handover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Deposit Modal */}
      <Dialog open={depositModal} onOpenChange={setDepositModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Bank Deposit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (₹) *</Label>
                <Input 
                  type="number"
                  value={depositForm.amount}
                  onChange={(e) => setDepositForm({...depositForm, amount: e.target.value})}
                  placeholder="Deposit amount"
                />
              </div>
              <div>
                <Label>Transaction Number *</Label>
                <Input 
                  value={depositForm.transaction_number}
                  onChange={(e) => setDepositForm({...depositForm, transaction_number: e.target.value})}
                  placeholder="Bank reference number"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Deposit Date *</Label>
                <Input 
                  type="date"
                  value={depositForm.deposit_date}
                  onChange={(e) => setDepositForm({...depositForm, deposit_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Bank Name</Label>
                <Input 
                  value={depositForm.bank_name}
                  onChange={(e) => setDepositForm({...depositForm, bank_name: e.target.value})}
                  placeholder="Bank name (optional)"
                />
              </div>
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea 
                value={depositForm.notes}
                onChange={(e) => setDepositForm({...depositForm, notes: e.target.value})}
                placeholder="Any additional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDepositModal(false)}>Cancel</Button>
            <Button onClick={submitDeposit} disabled={!depositForm.amount || !depositForm.transaction_number}>
              Record Deposit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Settlement Modal */}
      <Dialog open={settlementModal} onOpenChange={setSettlementModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Client Settlement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (₹) *</Label>
                <Input 
                  type="number"
                  value={settlementForm.amount}
                  onChange={(e) => setSettlementForm({...settlementForm, amount: e.target.value})}
                  placeholder="Settlement amount"
                />
              </div>
              <div>
                <Label>Transaction Number *</Label>
                <Input 
                  value={settlementForm.transaction_number}
                  onChange={(e) => setSettlementForm({...settlementForm, transaction_number: e.target.value})}
                  placeholder="Payment reference number"
                />
              </div>
            </div>
            <div>
              <Label>Settlement Date *</Label>
              <Input 
                type="date"
                value={settlementForm.settlement_date}
                onChange={(e) => setSettlementForm({...settlementForm, settlement_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea 
                value={settlementForm.notes}
                onChange={(e) => setSettlementForm({...settlementForm, notes: e.target.value})}
                placeholder="Any additional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettlementModal(false)}>Cancel</Button>
            <Button onClick={submitSettlement} disabled={!settlementForm.amount || !settlementForm.transaction_number}>
              Record Settlement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PharmeasyLayout>
  );
};

export default PharmeasyCODReconciliation;
